import React, { forwardRef, useCallback, useEffect, useState } from "react";

import classnames from "classnames";

import { Button } from "../Button/Button";
import { Checkbox } from "../Checkbox";
import { Dialog } from "../Dialog";
import { StyledConfirmation } from "./Styles";
import { Body2 } from "../Typography/Variants/Body2";

const CONFIRMATION_CHECKBOX_ID = "confirm-dialog-confirmation";

export interface ConfirmDialogProps {
  open?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  className?: string;
  dialogTitle?: string;
  dialogContent?: React.ReactNode;
  confirmBtnText?: string;
  cancelBtnText?: string;
  shouldShowCancelButton?: boolean;
  isLoading?: boolean;
  confirmationLabel?: string;
}

export const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(
  (
    {
      open = false,
      onCancel,
      onConfirm,
      className,
      dialogTitle = "Discard changes?",
      dialogContent = "All unsaved changes will be lost.",
      confirmBtnText = "OK",
      cancelBtnText = "Cancel",
      shouldShowCancelButton = true,
      isLoading = false,
      confirmationLabel,
    },
    ref
  ) => {
    const [confirmed, setConfirmed] = useState(false);
    const isConfirmBlocked = Boolean(confirmationLabel) && !confirmed;

    const handleToggleConfirmed = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) =>
        setConfirmed(event.target.checked),
      []
    );

    useEffect(() => {
      if (!open) {
        setConfirmed(false);
      }
    }, [open]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && onConfirm && !isConfirmBlocked) {
          event.preventDefault();
          onConfirm();
        }
      };

      if (open) {
        window.addEventListener("keydown", handleKeyDown);
      }

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [open, onConfirm, isConfirmBlocked]);

    return (
      <Dialog
        ref={ref}
        open={open}
        onClose={onCancel}
        className={classnames("c-confirm-dialog", className)}
        disableCloseOnEsc={isLoading}
      >
        <Dialog.Title>{dialogTitle}</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <Body2 lineHeight="loose" color="secondary" whitespace="pre-line">
            {dialogContent}
          </Body2>
          {confirmationLabel ? (
            <StyledConfirmation className="c-confirm-dialog--confirmation">
              <Checkbox
                id={CONFIRMATION_CHECKBOX_ID}
                checked={confirmed}
                onChange={handleToggleConfirmed}
                disabled={isLoading}
              />
              <label htmlFor={CONFIRMATION_CHECKBOX_ID}>
                <Body2 color="secondary">{confirmationLabel}</Body2>
              </label>
            </StyledConfirmation>
          ) : null}
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            variant="primary"
            style={{ marginRight: "12px" }}
            onClick={onConfirm}
            disabled={isLoading || isConfirmBlocked}
          >
            {confirmBtnText}
          </Button>
          {shouldShowCancelButton ? (
            <Button
              variant="secondary"
              data-action="cancel"
              onClick={onCancel}
              type="button"
              disabled={isLoading}
            >
              {cancelBtnText}
            </Button>
          ) : null}
        </Dialog.Actions>
      </Dialog>
    );
  }
);

ConfirmDialog.displayName = "ConfirmDialog";
